"use client"

import { useState, useEffect, useContext } from "react"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import styles from "./login.module.css"
import { Eye, EyeOff, Loader } from "lucide-react"
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google"
import { login } from "../../service/authService"
import { AuthContext } from "../../context/AuthContext"

const LoginForm = ({ onLoginSuccess }) => {
  const navigate = useNavigate()
  const [errorMessage, setErrorMessage] = useState("")
  const { setUser, setIsAuthenticated, login: authLogin } = useContext(AuthContext)
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm()

  useEffect(() => {
    const savedEmail = localStorage.getItem("email")
    const savedPassword = localStorage.getItem("password")
    const savedRememberMe = localStorage.getItem("rememberMe")

    if (savedRememberMe === "true") {
      setValue("emailOrPhone", savedEmail)
      setValue("password", savedPassword)
      setRememberMe(true)
    }
  }, [setValue])

  const onSubmit = async (data) => {
    setErrorMessage("")
    setLoading(true)

    const requestData = {
      email: data.emailOrPhone,
      password: data.password,
      guard: "company",
    }

    try {
      const response = await login(requestData)

      if (response?.status === true) {
        const token = response?.data?.token
        if (token) {
          localStorage.setItem("token", token)
          localStorage.setItem("isLoggedIn", "true")
        }

        const userData = JSON.stringify(response.data)
        localStorage.setItem("userData", userData)
        localStorage.setItem("userDataLast", userData)

        if (rememberMe) {
          localStorage.setItem("email", data.emailOrPhone)
          localStorage.setItem("password", data.password)
          localStorage.setItem("rememberMe", "true")
        } else {
          localStorage.removeItem("email")
          localStorage.removeItem("password")
          localStorage.removeItem("rememberMe")
        }

        // AuthContext-dən login funksiyasını istifadə et
        authLogin(token, response?.data)

        if (onLoginSuccess) {
          onLoginSuccess()
        }

        navigate("/")
      } else {
        setErrorMessage("Email və ya şifrə yanlışdır!")
      }
    } catch (error) {
      setErrorMessage("Xəta baş verdi. Zəhmət olmasa, yenidən cəhd edin.")
      console.error("Login error:", error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={styles.loginPage}>
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.formSide}>
            <div className={styles.formHeader}>
              <h1 className={styles.title}>Xoş gəldiniz!</h1>
              <p className={styles.subtitle}>Hesabınıza daxil olun</p>
            </div>

            <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
              <div className={styles.inputGroup}>
                <label htmlFor="emailOrPhone" className={styles.label}>
                  E-mail
                </label>
                <input
                  id="emailOrPhone"
                  className={`${styles.input} ${errors.email ? styles.inputError : ""}`}
                  placeholder="E-mail daxil edin"
                  {...register("emailOrPhone", {
                    required: "Email və ya telefon daxil edilməlidir",
                  })}
                />
                {errors.emailOrPhone && <p className={styles.errorMessage}>{errors.emailOrPhone.message}</p>}
              </div>

              <div className={styles.inputGroup}>
                <label htmlFor="password" className={styles.label}>
                  Şifrə
                </label>
                <div className={styles.passwordWrapper}>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    className={`${styles.input} ${errors.password ? styles.inputError : ""}`}
                    placeholder="********"
                    {...register("password", {
                      required: "Şifrə daxil edilməlidir",
                      minLength: {
                        value: 6,
                        message: "Şifrə ən azı 6 simvol olmalıdır",
                      },
                    })}
                  />
                  <button type="button" className={styles.eyeButton} onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.password && <p className={styles.errorMessage}>{errors.password.message}</p>}
              </div>

              {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}

              <div className={styles.rememberForgotRow}>
                <div className={styles.rememberMe}>
                  <input
                    type="checkbox"
                    id="rememberMe"
                    checked={rememberMe}
                    onChange={() => setRememberMe(!rememberMe)}
                    className={styles.checkbox}
                  />
                  <label htmlFor="rememberMe" className={styles.checkboxLabel}>
                    Yadda saxla
                  </label>
                </div>
              </div>

              <button type="submit" className={styles.submitButton} disabled={loading}>
                {loading ? (
                  <>
                    <Loader size={16} className={styles.spinner} /> Yüklənir...
                  </>
                ) : (
                  "Daxil ol"
                )}
              </button>
            </form>

            <div className={styles.divider}>
              <span className={styles.dividerText}>və ya</span>
            </div>

            <div className={styles.boxBottom}>
              <GoogleOAuthProvider>
                <GoogleLogin />
              </GoogleOAuthProvider>
            </div>
          </div>

          <div className={styles.imageSide}>{/* Buraya istəsən login üçün şəkil əlavə edə bilərsən */}</div>
        </div>
      </div>
    </div>
  )
}

export default LoginForm
